import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { findBestMove } from '../../utils/connect4AI';

interface Connect4Props {
  difficulty: 'easy' | 'medium' | 'hard';
  onWin: () => void;
  onLose: () => void;
  onDraw: () => void;
}

type CellValue = null | 'player' | 'ai';
type Board = CellValue[][];

const ROWS = 6;
const COLS = 7;

export function Connect4({ difficulty, onWin, onLose, onDraw }: Connect4Props) {
  const [board, setBoard] = useState<Board>(
    Array(ROWS).fill(null).map(() => Array(COLS).fill(null))
  );
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [lastMove, setLastMove] = useState<{row: number; col: number} | null>(null);
  const [winningCells, setWinningCells] = useState<{row: number; col: number}[]>([]);
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);

  const checkWin = (board: Board, row: number, col: number): boolean => {
    const player = board[row][col];
    if (!player) return false;

    // Check all directions
    const directions = [
      [0, 1],  // horizontal
      [1, 0],  // vertical
      [1, 1],  // diagonal right
      [1, -1]  // diagonal left
    ];

    for (const [dr, dc] of directions) {
      let count = 1;
      const winning = [{row, col}];

      // Check forward
      for (let i = 1; i < 4; i++) {
        const r = row + dr * i;
        const c = col + dc * i;
        if (r < 0 || r >= ROWS || c < 0 || c >= COLS) break;
        if (board[r][c] !== player) break;
        count++;
        winning.push({row: r, col: c});
      }

      // Check backward
      for (let i = 1; i < 4; i++) {
        const r = row - dr * i;
        const c = col - dc * i;
        if (r < 0 || r >= ROWS || c < 0 || c >= COLS) break;
        if (board[r][c] !== player) break;
        count++;
        winning.push({row: r, col: c});
      }

      if (count >= 4) {
        setWinningCells(winning);
        return true;
      }
    }

    return false;
  };

  const findLowestEmptyRow = (col: number): number => {
    for (let row = ROWS - 1; row >= 0; row--) {
      if (!board[row][col]) {
        return row;
      }
    }
    return -1;
  };

  const makeMove = (col: number) => {
    if (!isPlayerTurn || gameOver) return;

    const row = findLowestEmptyRow(col);
    if (row === -1) return;

    const newBoard = board.map(row => [...row]);
    newBoard[row][col] = 'player';
    setBoard(newBoard);
    setLastMove({row, col});

    if (checkWin(newBoard, row, col)) {
      setGameOver(true);
      onWin();
      return;
    }

    if (newBoard.every(row => row.every(cell => cell !== null))) {
      setGameOver(true);
      onDraw();
      return;
    }

    setIsPlayerTurn(false);
  };

  useEffect(() => {
    if (!isPlayerTurn && !gameOver) {
      const timer = setTimeout(() => {
        const aiCol = findBestMove(board, difficulty);
        const row = findLowestEmptyRow(aiCol);
        
        if (row !== -1) {
          const newBoard = board.map(row => [...row]);
          newBoard[row][aiCol] = 'ai';
          setBoard(newBoard);
          setLastMove({row, col: aiCol});

          if (checkWin(newBoard, row, aiCol)) {
            setGameOver(true);
            onLose();
          } else if (newBoard.every(row => row.every(cell => cell !== null))) {
            setGameOver(true);
            onDraw();
          } else {
            setIsPlayerTurn(true);
          }
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isPlayerTurn, gameOver, board, difficulty]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="bg-gray-800 p-4 rounded-lg">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="flex">
            {row.map((cell, colIndex) => (
              <button
                key={`${rowIndex}-${colIndex}`}
                className={`w-12 h-12 flex items-center justify-center border border-gray-700 relative
                  ${hoveredColumn === colIndex && !gameOver ? 'bg-gray-700' : 'bg-gray-800'}
                  ${lastMove?.row === rowIndex && lastMove?.col === colIndex ? 'ring-2 ring-yellow-400' : ''}
                  ${winningCells.some(pos => pos.row === rowIndex && pos.col === colIndex) ? 'ring-2 ring-green-400' : ''}`}
                onClick={() => makeMove(colIndex)}
                onMouseEnter={() => setHoveredColumn(colIndex)}
                onMouseLeave={() => setHoveredColumn(null)}
                disabled={!isPlayerTurn || gameOver}
              >
                {cell && (
                  <Shield
                    className={`w-8 h-8 ${
                      cell === 'player' ? 'text-cyan-400' : 'text-pink-500'
                    } fill-current transform transition-transform
                      ${lastMove?.row === rowIndex && lastMove?.col === colIndex ? 'scale-110' : ''}`}
                  />
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="text-cyan-400 font-mono">
        {gameOver ? (
          <span className="text-xl">Game Over!</span>
        ) : (
          <span>{isPlayerTurn ? 'Your Turn' : 'AI Thinking...'}</span>
        )}
      </div>
    </div>
  );
}